import { Component, OnInit, ViewChild } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { saveAs } from "file-saver/FileSaver";
import * as moment from "moment";
import { Message } from "primeng/components/common/api";
import { Observable } from "rxjs/Observable";
import "rxjs/add/observable/forkJoin";
import "rxjs/add/operator/toPromise";
import { Attachment } from "../../../shared/models/attachment";
import { Company } from "../../../shared/models/company";
import {
  CompanyType,
  CompanyTypeClass,
} from "../../../shared/models/company-type";
import { Location } from "../../../shared/models/location";
import { NextAction } from "../../../shared/models/next-action";
import { ObjectiveType } from "../../../shared/models/objective-type";
import { Person } from "../../../shared/models/person";
import { RequestForm } from "../../../shared/models/request-form";
import { RequestHistory } from "../../../shared/models/request-history";
import { RequestStatus } from "../../../shared/models/request-status";
import {
  RequestedType,
  RequestedTypeClass,
} from "../../../shared/models/requested-type";
import { Role } from "../../../shared/models/role";
import { AuthGuardService } from "../../../shared/services/auth-guard.service";
import { RequestFormService } from "./request-form.service";
import { validateRequestForm } from "./validator";

@Component({
  selector: "app-request-form",
  templateUrl: "./request-form.component.html",
  styleUrls: ["./request-form.component.css"],
})
export class RequestFormComponent implements OnInit {
  fg: FormGroup;
  personFG: FormGroup;

  detailPlaceHolder: string;
  acceptFileType: string;

  requestID: number;
  status: string;

  companyTypeEnum = CompanyType;
  companyTypes = CompanyTypeClass.list();
  requestTypes = RequestedTypeClass.list();
  objectiveTypes = ObjectiveType;
  requestStatus = RequestStatus;

  companies: Company[] = [];
  personCompanies: Company[] = [];
  personCompanyTypes = CompanyTypeClass.list();
  formCompanyType; // {key: string, value: string}
  formCompany;
  otherInstituteName: string;
  isOtherInstitute: boolean = false;
  isPersonOtherInstitute: boolean = false;

  companyListByType = {};

  locationOptions: Location[] = [];
  locations: Location[] = [];
  siteOptions: string[] = [];

  browsedAttachments: Attachment[] = [];
  browsedFiles: any[] = [];
  uploadedAttachments: Attachment[] = [];

  isActive: boolean = false;
  persons: Person[] = [];
  person: Person = new Person();
  editPersonIndex: number = -1;

  requestHistories: RequestHistory[] = [];

  minDate: Date;
  maxDate: Date;
  disableEndDateTime: boolean = true;

  personHistoryList: Person[] = [];
  selectedPersonHistory: Person;

  private sub: any;

  msgs: Message[];

  nextActionList;
  selectedNextAction;
  editable: boolean = true;
  onload: boolean = false;

  @ViewChild("inputUpload") inputUpload: any;
  disableCompanyType: boolean = false;
  disableCompany: boolean = false;

  @ViewChild("commentBox") commentBox: any;
  comment: string = "";
  isCancelRequest: boolean = false;
  disableSubmitButton: boolean = false;
  disableNextActionList: boolean = false;

  initCompanyComplete = false;
  hasEditPersonComplete = false;

  isExternal: boolean = true;
  userProfile;
  private formatDate = "YYYY-MM-DDTHH:mm:ss.SSS";

  constructor(
    private requestFormService: RequestFormService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private authGuardService: AuthGuardService,
    private router: Router,
  ) { }

  ngOnInit() {
    this.detailPlaceHolder =
      "Additional activity information or equipment detail (if any)/รายละเอียดอื่นๆ เช่น กำหนดการเข้าพื้นที่ หรือรายละเอียดอุปกรณ์ (ถ้ามี)";
    this.acceptFileType =
      "audio/*,video/*,image/*,.pdf,.doc,.docx,.xls,.xlsx,.zip";

    this.isExternal = this.authGuardService.hasRole(Role.DCA_MEMBER);
    this.userProfile = this.authGuardService.getUserProfileInformation();

    this.createFormGroup();
    this.createPersonFormGroup();
    this.initGroupLocations();
    this.storeRequestID();
    this.initRequestForm();
    this.handleSubmitButton();
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  storeRequestID() {
    this.sub = this.route.params.subscribe((params) => {
      this.requestID = +params["id"];
    });
  }

  initRequestForm() {
    if (this.requestID) {
      this.initCompany()
        .flatMap((res) => {
          this.storeInitCompany(res);
          return this.requestFormService.getRequestForm(this.requestID);
        })
        .subscribe(
          (form) => {
            this.formCompanyType = CompanyTypeClass.getObjectFromEnum(
              form.company.companyType
            );
            if (form.company) {
              this.fgCompany.setValue(form.company);
            }
            this.onChangeCompanyType(this.formCompanyType);

            this.locations = form.locations;
            this.persons = form.persons;
            this.requestHistories = form.requestHistories;
            this.storeRequestForm(form);
            this.onChangeDateTime();
            this.onChangeRequestedType();
            this.handleNextActions();
            this.handleFormEditable();
          },
          (error) => {
            console.log(error);
          }
        );
    } else {
      this.initCompany().subscribe((res) => {
        this.storeInitCompany(res);
        if (this.authGuardService.hasRole(Role.DCA_MEMBER)) {
          this.fgCompany.setValue(this.userProfile.company);
          this.iniCompanyForExternal();
        }
      });

      this.handleNextActions();
      this.onChangeRequestedType();
    }
  }

  iniCompanyForExternal() {
    this.disableCompany = true;
    this.formCompanyType = CompanyTypeClass.getObjectFromEnum(
      this.userProfile.company.companyType
    );
    this.onChangeCompanyType(this.formCompanyType);
  }

  createFormGroup() {
    this.fg = this.fb.group(
      {
        id: null,
        subject: "",
        company: this.fb.group({
          id: null,
          companyId: null,
          nameEn: "",
          symbol: "",
          companyType: "",
        }),
        otherCompanyName: "",
        site: ["DC1", Validators.required],
        locations: [[], Validators.required],
        requestedType: [{ value: "", disabled: false }, Validators.required],
        objectiveType: [{ value: "", disabled: true }, Validators.required],
        startDateTime: ["", Validators.required],
        endDateTime: [{ value: "", disabled: true }, Validators.required],
        detail: "",
        notiEmailReceiver: "",
        attachments: [],
        lastUpdate: null,
        createDate: null,
        persons: [[], Validators.required],
        requestHistories: [],
        duration: "",
        creator: null,
        requestStatus: null,
        nextAction: null,
      },
      { validator: [this.validateRequestForm, validateRequestForm] }
    );
    this.fg.patchValue({
      attachments: [],
      persons: [],
    });

    this.fg.valueChanges.subscribe((data) => this.handleSubmitButton());
  }

  createPersonFormGroup() {
    this.personFG = this.fb.group(
      {
        id: "",
        personHistory: null,
        name: ["", Validators.required],
        idType: ["", Validators.required],
        identification: ["", Validators.required],
        company: this.fb.group({
          id: ["", [Validators.required]],
          companyId: null,
          nameEn: "",
          symbol: "",
          companyType: "",
        }),
        otherCompanyName: "",
        phoneNumber: "",
        email: "",
        carDetail: "",
        checkIn: "",
        checkOut: "",
        duration: "",
      },
      { validator: this.validatePersonForm.bind(this) }
    );
  }

  validateRequestForm = (
    control: AbstractControl
  ): { [key: string]: boolean } => {
    const companyId = control.get("company.id");
    const otherCompanyName = control.get("otherCompanyName");
    const requestedType = control.get("requestedType");
    const objectiveType = control.get("objectiveType");


    let otherCompany = this.getCompanyById(companyId.value, this.companies);
    if (
      otherCompany != null &&
      otherCompany.companyId == 999999 &&
      otherCompanyName.value == ""
    ) {
      return { invalidOtherCompany: true };
    }

    let isEmptyRequestType =
      requestedType.value == "" || requestedType.value == null;
    if (requestedType.value == "" || requestedType.value == null) {
      return { invalidRequestedType: true };
    }

    if (
      !isEmptyRequestType &&
      RequestedType[requestedType.value] == RequestedType.C &&
      objectiveType.value == ""
    ) {
      return { invalidObjectiveType: true };
    }
  };

  validatePersonForm = (
    control: AbstractControl
  ): { [key: string]: boolean } => {
    const idType = control.get("idType");
    const identification = control.get("identification");
    const email = control.get("email");
    const companyId = control.get("company.id");
    const otherCompanyName = control.get("otherCompanyName");
    if (
      idType.value == "C" &&
      identification.value != null &&
      !this.validateCitizenId(identification.value)
    ) {
      return { invalidCitizenId: true };
    }
    if (!this.validateEmail(email.value)) {
      return { invalidEmail: true };
    }
    let otherCompany = this.getCompanyById(
      companyId.value,
      this.personCompanies
    );
    if (
      otherCompany != null &&
      otherCompany.companyId == 999999 &&
      otherCompanyName.value.trim() == ""
    ) {
      return { invalidOtherCompany: true };
    }

  };

  validateCitizenId(id) {
    if (id.length != 13) return false;
    for (var i = 0, sum = 0; i < 12; i++)
      sum += parseFloat(id.charAt(i)) * (13 - i);
    if ((11 - (sum % 11)) % 10 != parseFloat(id.charAt(12))) return false;
    return true;
  }

  validateEmail(email) {
    if (email != "" && email != null) {
      var re =
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(email);
    } else {
      return true;
    }
  }

  initGroupLocations() {
    this.requestFormService.listLocations().subscribe((locations) => {
      const sites = locations.filter((location) => location.active).map((location) => location.site);
      this.siteOptions = Array.from<string>(new Set(sites)); //['DC1', 'DC2']
    });
    if (this.requestID) return;
    this.initLocations('DC1');
  }

  initLocations(site: string) {
    this.requestFormService.listLocations().subscribe((locations) => {
      this.locationOptions = locations.filter((location) => location.active && location.site === site);
    });
  }

  onChangeSite() {
    this.initLocations(this.fgSite.value);
    this.fgLocation.reset([], { emitEvent: false });
    this.locations = [];
  }

  onChangeLocation(event) {
    const isChecked = event.target.checked;
    const id: number = event.target.value;

    if (isChecked) {
      const location: Location = this.locationOptions
        .filter((location) => location.id == id)
        .pop();
      this.locations.push(location);
    } else {
      this.locations = this.locations.filter((location) => location.id != id);
    }

    this.fg.patchValue({
      locations: this.locations,
    });
  }


  initCompany() {
    return Observable.forkJoin(
      this.requestFormService.listCompanyByType("B"),
      this.requestFormService.listCompanyByType("V"),
      this.requestFormService.listCompanyByType("Y"),
      this.requestFormService.listCompanyByType("Z")
    );
  }

  storeInitCompany(res) {
    this.companyListByType["B"] = res[0];
    this.companyListByType["V"] = res[1];
    this.companyListByType["Y"] = res[2];
    this.companyListByType["Z"] = res[3];

    this.companyListByType["B"].unshift({
      nameEn: "Select Company",
      symbol: "",
      id: '',
    });
    this.companyListByType["V"].unshift({
      nameEn: "Select Company",
      symbol: "",
      id: '',
    });
    this.companyListByType["Y"].unshift({
      nameEn: "Select Company",
      symbol: "",
      id: '',
    });
    this.companyListByType["Z"].unshift({
      nameEn: "Select Company",
      symbol: "",
      id: '',
    });

    let companyType = "B";
    if (this.formCompanyType) {
      companyType = this.formCompanyType;
    }
    this.companies = this.companyListByType[companyType];
    this.personCompanies = this.companyListByType[companyType];
  }

  onBrowseFile(event) {
    this.browsedFiles = [];
    let uploadedFileCount = this.uploadedAttachments.length;
    let browsedFileCount = this.browsedFiles.length;
    let eventFileCount = event.files.length;
    let total = uploadedFileCount + browsedFileCount + eventFileCount;

    if (total <= 10) {
      for (let file of event.files) {
        if (file.size > 0) {
          this.browsedFiles.push(file);
        }
      }
    } else {
      alert("Max file = 10");
      this.inputUpload.clear();
    }
  }

  onRemoveFile(event) {
    const selectedFile = event.file;
    this.browsedFiles = this.browsedFiles.filter(
      (file) => file !== selectedFile
    );
  }

  onChangeCompanyType(companyType) {
    let type;
    if (companyType != undefined) {
      type = companyType.key;
    } else {
      type = "B";
    }

    this.companies = this.companyListByType[type];
    this.onChangeCompany(this.fgCompanyId.value);

    if (this.isExternal) {
      let userCompany = this.userProfile.company;
      this.disableCompanyType = true;
      this.fgCompany.setValue(userCompany);
      this.companies = [userCompany];
      this.personCompanies = [userCompany];
      const defaultCompany = new Company();
      defaultCompany.nameEn = "Select Company";
      defaultCompany.symbol = "";
      defaultCompany.id = '';
      this.personCompanies.unshift(defaultCompany);

      if (CompanyType[userCompany.companyType] == CompanyType.V) {
        this.personCompanyTypes = CompanyTypeClass.list().filter(
          (type) => type.value != CompanyType.Z && type.value != CompanyType.B
        );
      } else {
        this.personCompanyTypes = CompanyTypeClass.list().filter(
          (type) => type.value != CompanyType.Z
        );
      }
    } else {
      if (this.editable) {
        this.fg.get("otherCompanyName").setValue("");
        this.fgCompanyId.setValue(null);
      }
    }

    this.initCompanyComplete = true;
  }

  toggleOtherInstitute(show: boolean) {
    if (this.isOtherInstitute != show && this.initCompanyComplete) {
      this.fg.get("otherCompanyName").setValue("");
    }
    this.isOtherInstitute = show;
  }

  togglePersonOtherInstitute(show: boolean) {
    if (
      this.isPersonOtherInstitute != show &&
      this.initCompanyComplete &&
      this.hasEditPersonComplete
    ) {
      this.personFG.get("otherCompanyName").setValue("");
    }
    this.isPersonOtherInstitute = show;
  }

  onChangePersonCompanyType(value) {
    const type = value;
    this.personCompanies = this.companyListByType[type];
    this.onChangePersonCompany(this.personFG.get("company").get("id").value);
    this.personFG.get("otherCompanyName").setValue("");
    this.personCompanyId.setValue(null);
    if (this.isExternal && CompanyType[type] == CompanyType.B) {
      let userCompany = this.userProfile.company;
      this.personCompanies = [userCompany];
      let defaultCompany = new Company();
      defaultCompany.nameEn = "Select Company";
      defaultCompany.symbol = "";
      defaultCompany.id = '';
      this.personCompanies.unshift(defaultCompany);
    }
    this.hasEditPersonComplete = true;
  }

  onChangePersonCompany(value) {
    let toShowOther = false;
    if (value) {
      let company = this.personCompanies.filter(
        (company) => company.id == value
      )[0];
      if (company) {
        if (company.companyId == 999999) {
          toShowOther = true;
        }
      }
    }
    this.personCompanyId.markAsTouched();
    this.togglePersonOtherInstitute(toShowOther);
  }

  onSave() {
    let formData = this.prepareFormData();
    this.toggleOnLoadButton();

    this.requestFormService.saveRequestForm(formData).subscribe(
      (resultRequestForm) => {
        console.log("resultRequestForm: ", resultRequestForm);
        this.toggleOnLoadButton();
        this.storeRequestForm(resultRequestForm);
        this.clearBrowsedFiles();
        this.comment = "";
        this.commentBox.clearComment();
        this.requestHistories = resultRequestForm.requestHistories;
        this.showSavedMessage(this.requestID, this.status, "Save form");
      },
      (error) => {
        this.toggleOnLoadButton();
        this.showErrorMessage(this.requestID, this.status, "Save form");
        console.log(error);
      }
    );
  }

  toggleOnLoadButton() {
    this.onload = !this.onload;
  }

  convertDate(resultRequestForm) {
    const startDateFromBase = moment.parseZone(resultRequestForm.startDateTime);
    const endDateFromBase = moment.parseZone(resultRequestForm.endDateTime);

    const startDate = moment(startDateFromBase.format(this.formatDate));
    const endDate = moment(endDateFromBase.format(this.formatDate));
    resultRequestForm.startDateTime = new Date(startDate.toDate());
    resultRequestForm.endDateTime = new Date(endDate.toDate());
    this.fg.patchValue({
      startDateTime: resultRequestForm.startDateTime,
      endDateTime: resultRequestForm.endDateTime,
    });
  }

  storeRequestForm(resultRequestForm) {
    const { site } = resultRequestForm;
    this.convertDate(resultRequestForm);
    this.initLocations(site);
    this.fg.patchValue(resultRequestForm);
    this.uploadedAttachments = resultRequestForm.attachments;
    this.requestID = resultRequestForm.id;
    this.status = this.requestStatus[resultRequestForm.requestStatus];
    this.editable = resultRequestForm.editable;
  }

  onChangeDateTime() {
    const startDateTime = this.fg.get("startDateTime").value;
    const condition = startDateTime === "" ? 'disable' : 'enable';
    this.fg.get("endDateTime")[condition]({ emitEvent: false });
  }

  prepareFormData() {
    this.fg.patchValue({
      attachments: this.uploadedAttachments,
      requestHistories: this.requestHistories,
    });

    let requestForm = this.fg.getRawValue() as RequestForm;
    if (requestForm.startDateTime) {
      requestForm.startDateTime =
        moment(requestForm.startDateTime).format(this.formatDate) +
        moment().utcOffset("+07:00").format("Z");
    }
    if (requestForm.endDateTime) {
      requestForm.endDateTime =
        moment(requestForm.endDateTime).format(this.formatDate) +
        moment().utcOffset("+07:00").format("Z");
    }

    let formData = new FormData();
    formData.append("jsonData", JSON.stringify(requestForm));
    formData.append("id", `${this.requestID}`);
    formData.append("comment", this.comment);

    this.browsedFiles.map((file) => {
      formData.append("files", file, file.name);
    });
    this.browsedFiles = [];

    return formData;
  }

  showSavedMessage(requestID: number, status: string, action: string) {
    this.msgs = [];
    this.msgs.push({
      severity: "success",
      summary: `${action} (ID: ${requestID}).`,
      detail: `status: ${status}`,
    });
  }

  showErrorMessage(requestID: number, status: string, action: string) {
    this.msgs = [];
    this.msgs.push({
      severity: "error",
      summary: `${action} (ID: ${requestID}).`,
      detail: `status: ${status}`,
    });
  }

  clearBrowsedFiles() {
    this.inputUpload.clear();
  }

  savePerson() {
    let person: Person = this.personFG.value;
    let personCompany;

    personCompany = this.getCompanyById(
      person.company.id,
      this.personCompanies
    );

    person.company = personCompany;

    if (this.editPersonIndex > -1) {
      this.persons[this.editPersonIndex] = person;
    } else {
      // new person
      this.persons.push(person);
    }
    this.fg.patchValue({
      persons: this.persons,
    });

    this.toggleModal();
    this.clearPerson();
  }

  getCompanyById(id: string, companies: Company[]) {
    let company = null;
    companies.forEach((c) => {
      if (+c.id === +id) {
        company = c;
      }
    });
    return company;
  }

  clearPerson() {
    this.personFG.reset();
    this.editPersonIndex = -1;
  }

  editPerson(person, index) {
    this.editPersonIndex = index;
    this.onChangePersonCompanyType(person.company.companyType);
    this.personFG.patchValue(person);
    const { companyId } = person.company;
    if (companyId === 999999) {
      this.personFG.get("otherCompanyName").setValue(person.otherCompanyName);
      this.isPersonOtherInstitute = true;
    }

    this.hasEditPersonComplete = false;
    this.toggleModal();
  }

  removePerson(person) {
    this.persons = this.persons.filter((p) => p !== person);
    this.fg.patchValue({
      persons: this.persons,
    });
  }

  closePersonModal() {
    this.toggleModal();
    this.clearPerson();
  }

  toggleModal() {
    this.isActive = !this.isActive;
  }

  removeUploadedAttachment(uploadedAttachment: Attachment) {
    this.uploadedAttachments = this.uploadedAttachments.filter(
      (attachment) => attachment !== uploadedAttachment
    );
  }

  getURLAttachment(uploadedAttachment: Attachment) {
    this.requestFormService
      .downloadAttachment(uploadedAttachment.id)
      .subscribe((response) => this.download(response));
  }

  download(response) {
    const contentDispositionHeader: string = response.headers.get(
      "Content-Disposition"
    );
    const parts: string[] = contentDispositionHeader.split(";");
    const filename = parts[1].split("=")[1];
    const blob = new Blob([response.body], {
      type: "application/octet-stream",
    });
    saveAs(blob, filename);
  }

  onChangeRequestedType() {
    let ctrlRequestedType = this.fg.get("requestedType");
    let ctrlObjectiveType = this.fg.get("objectiveType");

    if (this.isExternal) {
      this.requestTypes = RequestedTypeClass.list().filter(
        (type) => type.value == RequestedType.C
      );
    }
    if (RequestedType[ctrlRequestedType.value] == RequestedType.C) {
      ctrlObjectiveType.enable();
    } else {
      if (ctrlRequestedType.value != "") {
        ctrlObjectiveType.setValue("OTHER");
      } else {
        ctrlObjectiveType.setValue("");
      }
      ctrlObjectiveType.disable();
    }
  }

  checkLocation(id: number) {
    let isChecked = false;
    this.locations.forEach((location) => {
      if (location.id == id) {
        isChecked = true;
      }
    });
    return isChecked;
  }

  handleNextActions() {
    let isNewForm = this.fgCompanyId.value == null;
    if (isNewForm) {
      let submitTaskAction = new NextAction();
      submitTaskAction.action = "submit";
      submitTaskAction.label = "Submit Request";
      let nextActionList = [submitTaskAction];
      this.nextActionList = nextActionList;
    } else {
      this.nextActionList = this.fgNextActionList.value;
      this.disableNextActionList = this.fgNextActionList.value && this.fgNextActionList.value.length < 1;
    }
  }

  handleFormEditable() {
    if (!this.editable) {
      this.fg.disable();
    }
  }

  onSubmit() {
    if (this.selectedNextAction) {
      let formData = this.prepareFormData();
      this.toggleOnLoadButton();

      this.requestFormService
        .doAction(this.selectedNextAction, formData)
        .subscribe(
          (form) => {
            if (form) {
              this.toggleOnLoadButton();
              this.router.navigate(["request-list"]);
            }
          },
          (error) => {
            this.toggleOnLoadButton();
            this.showErrorMessage(this.requestID, this.status, "Submit form");
            console.log(error);
          }
        );
    }
  }

  onChangeNextAction() {
    if (this.selectedNextAction == NextAction.CANCEL_TASK) {
      this.isCancelRequest = true;
    } else {
      this.isCancelRequest = false;
    }
    this.handleSubmitButton();
  }

  onChangeComment(comment: string) {
    this.comment = comment;
    this.handleSubmitButton();
  }

  handleSubmitButton() {
    this.disableSubmitButton =
      (!this.selectedNextAction && !this.isCancelRequest) ||
      (this.isCancelRequest && this.comment == "") ||
      (this.editable && !this.fg.valid);
  }

  saveComment(comment: string) {
    let formData = this.prepareFormData();
    this.toggleOnLoadButton();
    this.requestFormService.saveComment(formData).subscribe(
      (form) => {
        this.toggleOnLoadButton();
        this.requestHistories = form.requestHistories;
        this.convertDate(form);
        this.comment = "";
        this.commentBox.clearComment();
        this.showSavedMessage(
          form.id,
          RequestStatus[form.requestStatus],
          "Send Comment"
        );
      },
      (error) => {
        this.toggleOnLoadButton();
        console.log(error);
      }
    );
  }

  onChangeCompany(value) {
    let toShowOther = false;
    if (value) {
      let company = this.companies.filter((company) => company.id == value)[0];
      if (company) {
        this.fgCompany.setValue(company);
        if (company.companyId == 999999) {
          toShowOther = true;
        } else {
          this.requestFormService.listPersonHistory().subscribe(
            (personHistory) => {
              this.personHistoryList = personHistory as Person[];
            },
            (error) => {
              console.log(error);
            }
          );
        }
      }
    } else {
      this.personHistoryList = [];
    }
    this.toggleOtherInstitute(toShowOther);
  }

  selectPersonHistory(value: Person) {
    value.id = null;
    value.checkIn = null;
    value.checkOut = null;
    this.personFG.patchValue(value);

    let companyList = this.companyListByType[value.company.companyType];
    let company = companyList.filter(
      (company) => company.companyId == value.company.companyId
    )[0];
    this.onChangePersonCompanyType(value.company.companyType);
    this.personFG.patchValue({
      company: company,
    });
  }

  getTooltipPermittedAccessTime() {
    return `- Weekdays: from 18:00 to 6:00 of the next day/ วันทำการ: ระหว่าง 18.00 - 06.00 น. ของวันถัดไป

    - Weekend & Holiday: from 18:00 of last trading day to 18:00 of last holiday/ วันหยุดทำการ: ระหว่าง 18.00 น. ของวันทำการสุด ถึง 18.00 น. ของวันทำการสุดท้าย`;
  }

  get fgCompany() {
    return this.fg.get("company");
  }
  get fgCompanyId() {
    return this.fgCompany.get("id");
  }
  get fgCompanyName() {
    return this.fgCompany.get("nameEn");
  }
  get fgLocation() {
    return this.fg.get("locations");
  }
  get fgSite() {
    return this.fg.get("site");
  }
  get fgRequestedType() {
    return this.fg.get("requestedType");
  }
  get fgObjectiveType() {
    return this.fg.get("objectiveType");
  }
  get fgStartDateTime() {
    return this.fg.get("startDateTime");
  }
  get fgEndDateTime() {
    return this.fg.get("endDateTime");
  }
  get fgNextActionList() {
    return this.fg.get("nextAction");
  }

  get personName() {
    return this.personFG.get("name");
  }
  get personId() {
    return this.personFG.get("identification");
  }
  get personIdType() {
    return this.personFG.get("idType");
  }
  get personCompany() {
    return this.personFG.get("company");
  }
  get personCompanyId() {
    return this.personCompany.get("id");
  }
  get personCompanyName() {
    return this.personCompany.get("nameEn");
  }
  get personCompanyType() {
    return this.personCompany.get("companyType");
  }
  get personEmail() {
    return this.personFG.get("email");
  }
  get personList() {
    return this.fg.get("persons");
  }
}



// WEBPACK FOOTER //
// /app/src/app/modules/request/request-form/request-form.component.ts